import React from 'react';
import {
  Box, Card, CardContent, Collapse, IconButton, LinearProgress, Stack,
  Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Stage labels/blurbs by id. The ORDER comes from the run doc itself
// (config_snapshot.stage_order, pinned at run creation) so historical
// runs keep rendering exactly the stages they actually ran.
export const STAGE_META = {
  profile: { label: 'Profile', blurb: 'Measure the data\u2019s character; pick settings' },
  represent: { label: 'Represent', blurb: 'Build the point-in-time data grid' },
  assemble: { label: 'Assemble', blurb: 'Signals + your features + event channels' },
  generate: { label: 'Generate', blurb: 'Features chosen by the data\u2019s profile' },
  screen: { label: 'Screen', blurb: 'Statistical screen against calibrated nulls' },
  prune: { label: 'Prune', blurb: 'Drop redundant candidates' },
  characterize: { label: 'Characterize', blurb: 'Shape, sign and lag per surviving driver' },
  segment_compile: { label: 'Segment', blurb: 'Compile the goal segment + parity gate' },
  lift: { label: 'Lift', blurb: 'Does it actually improve prediction? (walk-forward)' },
  fdr_stability: { label: 'Validate', blurb: 'False-discovery control + stability' },
  consolidate: { label: 'Consolidate', blurb: 'Write validated relationships' },
  expand: { label: 'Pathways', blurb: 'Multi-hop chains (drivers of drivers)' },
};

export const DEFAULT_ORDER = [
  'profile', 'represent', 'screen', 'prune', 'characterize', 'lift',
  'fdr_stability', 'consolidate', 'expand',
];

const StageIcon = ({ status, theme }) => {
  switch (status) {
    case 'completed':
      return <CheckCircleOutlineIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />;
    case 'failed':
      return <ErrorOutlineIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />;
    case 'skipped':
      return <RemoveCircleOutlineIcon sx={{ color: theme.palette.text.disabled, fontSize: 20 }} />;
    case 'running':
      return (
        <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center' }}>
          <LinearProgress sx={{ width: 20, height: 4, borderRadius: 2 }} />
        </Box>
      );
    default:
      return <RadioButtonUncheckedIcon sx={{ color: theme.palette.text.disabled, fontSize: 20 }} />;
  }
};

const countText = (stage) => {
  const inC = stage?.in_count;
  const outC = stage?.out_count;
  if (inC == null && outC == null) return null;
  if (inC != null && outC != null) return `${inC} → ${outC}`;
  return `${outC ?? inC}`;
};

// Meta keys that already have a rich, dedicated renderer below (or are shown
// in the row header) — everything else falls through to the generic block so
// no expanded stage is ever blank.
const META_HANDLED = new Set([
  'summary', 'reasons', 'resolved', 'parity', 'channel_groups', 'by_origin',
  'recall', 'transfer', 'user_features', 'event_channels', 'transforms',
  'generated_columns', 'dropped', 'traits', 'top_survivors', 'top_importance',
  'reason', 'duration_s',
  // Consolidate gets its own friendly one-liner.
  'new_edges', 'broken', 'broken_paths', 'exploratory', 'archived',
  'segment_finalized',
]);

const humanizeKey = (k) => k.replace(/_/g, ' ');

const fmtMetaValue = (v) => {
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (Array.isArray(v)) {
    return v.every((x) => x == null || typeof x !== 'object')
      ? v.join(', ') : `${v.length}`;
  }
  if (v && typeof v === 'object') {
    const parts = Object.entries(v)
      .filter(([, sv]) => sv != null && typeof sv !== 'object')
      .map(([sk, sv]) => `${humanizeKey(sk)} ${sv}`);
    return parts.length ? parts.join(' · ') : `${Object.keys(v).length}`;
  }
  return String(v);
};

// Any meta entry not covered by a dedicated renderer, so every stage shows
// at least its own numbers when expanded.
const genericMetaEntries = (meta) => Object.entries(meta || {}).filter(
  ([k, v]) => {
    if (META_HANDLED.has(k)) return false;
    if (v == null) return false;
    if (Array.isArray(v) && v.length === 0) return false;
    if (typeof v === 'object' && !Array.isArray(v)
      && Object.keys(v).length === 0) return false;
    return true;
  },
);

/**
 * Vertical funnel of run stages. ``run`` is the discovery_runs doc
 * (stages keyed by id with status/in_count/out_count/rejected/meta/error).
 */
const RunFunnel = ({ run, embedded = false }) => {
  const theme = useTheme();
  const [openStage, setOpenStage] = React.useState(null);
  const stages = run?.stages || {};
  const order = run?.config_snapshot?.stage_order || DEFAULT_ORDER;
  const STAGES = order.map((id) => ({
    id,
    label: STAGE_META[id]?.label || id,
    blurb: STAGE_META[id]?.blurb || '',
  }));

  const body = (
    <CardContent sx={{ py: 2 }}>
        <Stack spacing={0}>
          {STAGES.map((meta, i) => {
            const stage = stages[meta.id] || {};
            const status = stage.status || 'pending';
            const counts = countText(stage);
            const rejected = stage.rejected || {};
            const rejectedTotal = Object.values(rejected).reduce((a, b) => a + (b || 0), 0);
            const metaKeys = Object.keys(stage.meta || {}).filter((k) => k !== 'duration_s');
            const expandable = status !== 'pending'
              && (rejectedTotal > 0 || stage.error || metaKeys.length > 0);
            const open = openStage === meta.id;

            return (
              <Box key={meta.id}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  onClick={() => expandable && setOpenStage(open ? null : meta.id)}
                  sx={{
                    py: 1.1,
                    px: 1,
                    borderRadius: 1,
                    cursor: expandable ? 'pointer' : 'default',
                    backgroundColor: status === 'running'
                      ? alpha(theme.palette.info.main, 0.06) : 'transparent',
                    '&:hover': expandable
                      ? { backgroundColor: alpha(theme.palette.text.primary, 0.03) } : {},
                  }}
                >
                  <StageIcon status={status} theme={theme} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: status === 'running' ? 600 : 500,
                          color: status === 'pending' ? 'text.disabled' : 'text.primary',
                        }}
                      >
                        {meta.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {meta.blurb}
                      </Typography>
                    </Stack>
                  </Box>
                  {counts && (
                    <Tooltip title="candidates in → out" arrow>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        {counts}
                      </Typography>
                    </Tooltip>
                  )}
                  {stage?.meta?.duration_s != null && (
                    <Typography variant="caption" color="text.disabled">
                      {stage.meta.duration_s}s
                    </Typography>
                  )}
                  {expandable && (
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  )}
                </Stack>

                <Collapse in={open}>
                  <Box sx={{ ml: 5.5, mr: 1, mb: 1.25, p: 1.25, borderRadius: 1, backgroundColor: alpha(theme.palette.text.primary, 0.03) }}>
                    {stage.error && (
                      <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 0.5 }}>
                        {stage.error}
                      </Typography>
                    )}
                    {rejectedTotal > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Rejected: {Object.entries(rejected)
                          .filter(([, v]) => v > 0)
                          .map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`).join(' · ')}
                      </Typography>
                    )}
                    {(stage.meta?.summary || []).length > 0 && (
                      <Box sx={{ mb: 0.75 }}>
                        {stage.meta.summary.map((line, j) => (
                          <Typography key={j} variant="caption" sx={{ display: 'block' }}>
                            {line}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {stage.meta?.reasons && Object.keys(stage.meta.reasons).length > 0 && (
                      <Box sx={{ mb: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Auto-picked settings:
                        </Typography>
                        {Object.entries(stage.meta.reasons).map(([k, why]) => (
                          <Typography key={k} variant="caption" sx={{ display: 'block' }}>
                            <Box component="span" sx={{ fontFamily: 'monospace' }}>
                              {k.replace(/_/g, ' ')}
                              {stage.meta.resolved?.[k] != null ? ` = ${String(stage.meta.resolved[k])}` : ''}
                            </Box>
                            {' — '}{why}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {stage.meta?.parity && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mb: 0.5, color: stage.meta.parity.status === 'passed' ? 'success.main' : 'error.main' }}
                      >
                        Parity gate {stage.meta.parity.status}: {stage.meta.parity.columns_checked} columns
                        {' '}× {stage.meta.parity.rows_checked} rows verified against the compiled segment
                      </Typography>
                    )}
                    {stage.meta?.channel_groups && Object.keys(stage.meta.channel_groups).length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Segment channels: {Object.entries(stage.meta.channel_groups)
                          .filter(([, v]) => v > 0)
                          .map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`).join(' · ')}
                      </Typography>
                    )}
                    {stage.meta?.by_origin && Object.keys(stage.meta.by_origin).length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Candidates: {Object.entries(stage.meta.by_origin)
                          .map(([k, v]) => `${v} ${k}${v === 1 ? '' : 's'}`).join(' · ')}
                      </Typography>
                    )}
                    {stage.meta?.recall?.applied && (
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'info.main' }}>
                        Vector recall: universe of {stage.meta.recall.n_candidates} exceeded the
                        {' '}{stage.meta.recall.screen_budget}-channel screen budget —
                        {' '}{stage.meta.recall.recalled_signals} most similar signals proposed
                        {' '}({stage.meta.recall.dropped_signals} set aside), events and your features always compete.
                        Embeddings v{stage.meta.recall.embedding_version}; shortlist snapshotted for reproducibility.
                      </Typography>
                    )}
                    {stage.meta?.recall && !stage.meta.recall.applied
                      && stage.meta.recall.reason
                      && String(stage.meta.recall.reason).includes('unavailable') && (
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'warning.main' }}>
                        Vector recall skipped ({stage.meta.recall.reason}) — every candidate screened in full.
                      </Typography>
                    )}
                    {stage.meta?.transfer?.applied && (
                      <Box sx={{ mb: 0.75 }}>
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.25, color: 'info.main' }}>
                          Cross-goal transfer: borrowed {stage.meta.transfer.n_transferred} proven
                          {' '}feature-form{stage.meta.transfer.n_transferred === 1 ? '' : 's'} from
                          {' '}{stage.meta.transfer.donor_goals} similar goal{stage.meta.transfer.donor_goals === 1 ? '' : 's'}
                          {' '}— each still runs the full funnel on its own evidence.
                        </Typography>
                        {(stage.meta.transfer.forms || []).slice(0, 6).map((f, j) => (
                          <Typography key={j} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                            {f.form} — from {f.from_goal} (sim {Math.round((f.similarity || 0) * 100)}%)
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {(stage.meta?.user_features || []).length > 0 && (
                      <Box sx={{ mb: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Your features in the race:
                        </Typography>
                        {stage.meta.user_features.slice(0, 8).map((f, j) => (
                          <Typography key={j} variant="caption" sx={{ display: 'block' }}>
                            {f.display || f.name}
                            {(f.flags || []).includes('pit_safe_substituted')
                              ? ' — fill adjusted to be point-in-time safe' : ''}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {(stage.meta?.event_channels || []).length > 0 && (
                      <Box sx={{ mb: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Event channels in the race:
                        </Typography>
                        {stage.meta.event_channels.slice(0, 8).map((name, j) => (
                          <Typography key={j} variant="caption" sx={{ display: 'block' }}>
                            {name}
                          </Typography>
                        ))}
                        {stage.meta.event_channels.length > 8 && (
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                            +{stage.meta.event_channels.length - 8} more
                          </Typography>
                        )}
                      </Box>
                    )}
                    {(stage.meta?.transforms || []).length > 0 && (
                      <Box sx={{ mb: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Transform bank (chosen by the data&apos;s profile):
                        </Typography>
                        {stage.meta.transforms.map((t, j) => (
                          <Typography key={j} variant="caption" sx={{ display: 'block' }}>
                            <Box component="span" sx={{ fontFamily: 'monospace' }}>{t.name}</Box>
                            {' — '}{t.trait === 'always' ? 'always on' : `because: ${t.trait.replace(/_/g, ' ')}`}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {(stage.meta?.generated_columns || []).length > 0 && (
                      <Box sx={{ mb: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Generated columns:
                        </Typography>
                        {stage.meta.generated_columns.map((g, j) => (
                          <Typography key={j} variant="caption" sx={{ display: 'block' }}>
                            {g.display || g.key} ({g.trait.replace(/_/g, ' ')})
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {(stage.meta?.dropped || []).length > 0 && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.5 }}>
                        Dropped for budget: {stage.meta.dropped.map((d) => d.transform).join(', ')}
                      </Typography>
                    )}
                    {(stage.meta?.traits || []).length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Traits: {stage.meta.traits.join(' · ')}
                      </Typography>
                    )}
                    {(stage.meta?.top_survivors || []).length > 0 && (
                      <Box sx={{ mt: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Strongest so far:
                        </Typography>
                        {stage.meta.top_survivors.slice(0, 6).map((s, j) => (
                          <Typography key={j} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                            {s.key} ({s.transform}{s.mechanism === 'variance' ? ' · variance' : ''})
                            {' '}lag {s.lag} · corr {s.corr}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {(stage.meta?.top_importance || []).length > 0 && (
                      <Box sx={{ mb: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Strongest by probe importance:
                        </Typography>
                        {stage.meta.top_importance.slice(0, 6).map((s, j) => (
                          <Typography key={j} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                            {s.key}
                            {s.channel && s.channel !== 'level' ? ` · ${s.channel}` : ''}
                            {s.mechanism ? ` · ${s.mechanism}` : ''}
                            {' '}({s.direction}) — importance {s.importance}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {/* Consolidate: the relationship ledger for this run. */}
                    {(stage.meta?.new_edges != null
                      || stage.meta?.exploratory != null
                      || stage.meta?.archived != null) && (
                      <Box sx={{ mb: 0.75 }}>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {stage.out_count ?? 0} validated relationship
                            {(stage.out_count ?? 0) === 1 ? '' : 's'}
                          </Box>
                          {stage.meta.new_edges > 0 ? ` · ${stage.meta.new_edges} new` : ''}
                          {stage.meta.exploratory > 0 ? ` · ${stage.meta.exploratory} suggestive` : ''}
                          {stage.meta.broken > 0 ? ` · ${stage.meta.broken} broken` : ''}
                          {stage.meta.archived > 0 ? ` · ${stage.meta.archived} archived` : ''}
                        </Typography>
                        {stage.meta.exploratory > 0 && (
                          <Typography variant="caption" color="secondary.main" sx={{ display: 'block', mt: 0.25 }}>
                            {stage.meta.exploratory} suggestive relationship
                            {stage.meta.exploratory === 1 ? '' : 's'} kept for exploration —
                            drag the graph&rsquo;s strength knob down to see them.
                          </Typography>
                        )}
                        {stage.meta.segment_finalized && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Serving segment pruned to the validated drivers.
                          </Typography>
                        )}
                      </Box>
                    )}
                    {/* Generic catch-all: any remaining meta a stage reported,
                        so the panel always shows the run&rsquo;s own numbers. */}
                    {genericMetaEntries(stage.meta).length > 0 && (
                      <Box sx={{ mt: 0.25 }}>
                        {genericMetaEntries(stage.meta).map(([k, v]) => (
                          <Typography key={k} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            <Box component="span" sx={{ textTransform: 'capitalize' }}>
                              {humanizeKey(k)}
                            </Box>
                            {': '}{fmtMetaValue(v)}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {stage.meta?.reason && (
                      <Typography variant="caption" color="text.disabled">
                        {stage.meta.reason}
                      </Typography>
                    )}
                  </Box>
                </Collapse>

                {i < STAGES.length - 1 && (
                  <Box sx={{ ml: 2.2, height: 6, borderLeft: '1px dashed', borderColor: 'divider' }} />
                )}
              </Box>
            );
          })}
        </Stack>
      </CardContent>
  );

  if (embedded) return body;
  return <Card variant="outlined">{body}</Card>;
};

export default RunFunnel;
